import { Box } from '@mantine/core';
import { isIntegrationMetricWidget } from '../../../api';
import { WidgetType } from '../../../interfaces';
import type { ISampleOrActualMetricWidget } from '../constants';
import { MetricIntegrationBarChart } from './MetricIntegrationBarChart';
import { MetricIntegrationLineChart } from './MetricIntegrationLineChart';
import { MetricWidgetSingleValue } from './MetricWidgetSingleValue';
import { MetricWorkspaceLineChart } from './MetricWorkspaceLineChart';
import { MonitorChart } from './MonitorChart/MonitorChart';
import {
	MetricAIQueryTableWithoutUsers,
	MetricAIQueryTableWithUsers,
} from './Table/MetricAIQueryTable';
import { MetricGroupTable } from './Table/MetricGroupTable';
import { MetricQueriesTable } from './Table/MetricQueriesTable';
import { MetricResourceTable } from './Table/MetricResourceTable';
import { MetricSearchTermTable } from './Table/MetricSearchTermTable';
import { MetricTagTable } from './Table/MetricTagTable';
import { MetricUserTable } from './Table/MetricUserTable';
import { MetricWidgetTable } from './Table/MetricWidgetTable';

export type IMetricWidgetContentProps = ISampleOrActualMetricWidget;

export function MetricWidgetContent(props: IMetricWidgetContentProps) {
	const { metricWidget } = props;
	if (!metricWidget.metric_metadata) return null;

	if (metricWidget.type === WidgetType.METRIC_LINE_CHART) {
		if (!isIntegrationMetricWidget(metricWidget)) {
			return (
				<Box p="md">
					<MetricWorkspaceLineChart {...props} />
				</Box>
			);
		}
		return (
			<Box p="md">
				<MetricIntegrationLineChart {...props} />
			</Box>
		);
	}

	if (metricWidget.type === WidgetType.METRIC_BAR_CHART) {
		return (
			<Box p="md">
				<MetricIntegrationBarChart {...props} />
			</Box>
		);
	}

	if (metricWidget.type === WidgetType.METRIC_SINGLE_VALUE) {
		return (
			<Box p="md">
				<MetricWidgetSingleValue {...props} />
			</Box>
		);
	}

	if (metricWidget.type === WidgetType.MONITOR) {
		return (
			<Box p="md">
				<MonitorChart {...props} />
			</Box>
		);
	}

	if (metricWidget.type === WidgetType.METRIC_USER_TABLE) {
		let label = 'Actions';
		if (
			metricWidget.metric_metadata.metric_name === 'TOP_USERS' ||
			metricWidget.metric_metadata.metric_name === 'LEAST_ACTIVE_EDITORS'
		) {
			if (metricWidget.metric_metadata.fixed_filter?.type === 'view') {
				label = 'Views';
			} else {
				label = 'Edits';
			}
		} else if (
			metricWidget.metric_metadata.metric_name === 'TOP_USER_SEARCHES'
		) {
			label = 'Searches';
		} else if (
			metricWidget.metric_metadata.metric_name === 'TOP_RESOURCE_OWNERS'
		) {
			label = 'Resources';
		}

		return (
			<MetricWidgetTable
				TableComponent={MetricUserTable}
				infoColumns={[
					{
						label,
						fieldName: 'event_count',
					},
				]}
				{...props}
			/>
		);
	}
	if (metricWidget.type === WidgetType.METRIC_GROUP_TABLE) {
		let label = 'Edits';
		if (metricWidget.metric_metadata.metric_name === 'TOP_GROUPS_BY_ENTITIES') {
			let type = metricWidget.metric_metadata.fixed_filter?.type;
			label = type
				? type.charAt(0).toUpperCase() + type.slice(1) + 's'
				: 'Entities';
			if (label.slice(-2) === 'ys') {
				label = label.slice(0, -2) + 'ies';
			}
		} else {
			metricWidget.metric_metadata.fixed_filter?.type === 'view'
				? (label = 'Views')
				: (label = 'Edits');
		}
		return (
			<MetricWidgetTable
				TableComponent={MetricGroupTable}
				infoColumns={[
					{
						label,
						fieldName: 'event_count',
					},
				]}
				{...props}
			/>
		);
	}
	if (metricWidget.type === WidgetType.METRIC_RESOURCE_TABLE) {
		return (
			<MetricWidgetTable
				infoColumns={[
					{
						label: 'Views',
						fieldName: 'views',
					},
				]}
				TableComponent={MetricResourceTable}
				{...props}
			/>
		);
	}
	if (metricWidget.type === WidgetType.METRIC_SEARCH_TERM_TABLE) {
		return (
			<MetricWidgetTable
				infoColumns={[
					{
						label: 'Searches',
						fieldName: 'count',
					},
				]}
				TableComponent={MetricSearchTermTable}
				{...props}
			/>
		);
	}
	if (metricWidget.type === WidgetType.METRIC_QUERIES_TABLE) {
		return (
			<MetricWidgetTable
				infoColumns={[
					{
						label: 'Queries',
						fieldName: 'execution_count',
					},
				]}
				TableComponent={MetricQueriesTable}
				{...props}
			/>
		);
	}
	if (metricWidget.type === WidgetType.METRIC_TAG_TABLE) {
		return (
			<MetricWidgetTable
				infoColumns={[
					{
						label: 'Resources',
						fieldName: 'entity_count',
					},
				]}
				TableComponent={MetricTagTable}
				{...props}
			/>
		);
	}
	if (metricWidget.type === WidgetType.AI_CHAT_HISTORY) {
		if (props.source === 'sample') {
			return (
				<MetricWidgetTable
					infoColumns={[]}
					TableComponent={MetricAIQueryTableWithoutUsers}
					{...props}
				/>
			);
		}
		return (
			<MetricWidgetTable
				infoColumns={[]}
				TableComponent={MetricAIQueryTableWithUsers}
				{...props}
			/>
		);
	}

	return null;
}
